import { motion, AnimatePresence } from "framer-motion";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EmailPanel } from "@/components/EmailPanel";
import { Check, X, Mail, MapPin, Globe, ChevronUp, Building2, Send, Phone, User, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { saas } from "@/lib/api";

const relevanceStyles = {
  Høy: "bg-[#DCFCE7] text-[#15803d] border-[#16A34A]/30",
  Middels: "bg-[#FEF3C7] text-[#b45309] border-[#D97706]/30",
  Lav: "bg-slate-100 text-slate-500 border-slate-300 dark:bg-slate-700 dark:text-slate-300",
};

const statusRing = {
  godkjent: "ring-2 ring-[#16A34A]/40",
  avvist: "ring-2 ring-destructive/30 opacity-70",
  ikke_behandlet: "",
};

export const LeadCard = ({
  lead,
  compact,
  selected,
  onSelect,
  onStatus,
  expanded,
  onToggleEmail,
  onChange,
}) => {
  const contacted = lead.sent || lead.already_contacted;
  return (
    <motion.div
      layout
      data-testid="lead-card-item"
      data-contacted={contacted ? "true" : "false"}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.25 }}
      className={`rounded-2xl border border-border bg-card shadow-sm hover:shadow-md transition-shadow ${statusRing[lead.status] || ""} ${compact ? "p-3" : "p-5"} ${contacted ? "opacity-60 grayscale bg-muted/40" : ""}`}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          data-testid="lead-checkbox"
          checked={selected}
          onCheckedChange={() => onSelect(lead.id)}
          className="mt-1"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3
              data-testid="lead-company-name"
              className={`font-heading font-bold text-foreground truncate ${compact ? "text-sm" : "text-lg"}`}
            >
              {lead.name}
            </h3>
            {lead.sent && (
              <span className="flex items-center gap-1 text-[10px] font-semibold text-primary shrink-0">
                <Send className="h-3 w-3" /> SENDT
              </span>
            )}
            {!lead.sent && lead.already_contacted && (
              <span className="rounded bg-[#FEF3C7] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#b45309] shrink-0">
                Allerede kontaktet
              </span>
            )}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Badge
              data-testid="lead-industry-badge"
              variant="outline"
              className="gap-1 text-[11px] font-medium border-border bg-secondary text-secondary-foreground"
            >
              <Building2 className="h-3 w-3" /> {lead.type}
            </Badge>
            <Badge
              variant="outline"
              className={`text-[11px] font-semibold ${relevanceStyles[lead.relevance] || ""}`}
            >
              {lead.relevance}
            </Badge>
          </div>

          <div className={`mt-2 space-y-1 ${compact ? "text-xs" : "text-sm"} text-muted-foreground`}>
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span>{lead.area}</span>
              {lead.distance_km > 0 && (
                <>
                  <span className="text-border">·</span>
                  <span className="font-medium text-foreground">{lead.distance_km} km unna</span>
                </>
              )}
            </div>
            {lead.website && (
              <div className="flex items-center gap-1.5 truncate">
                <Globe className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="truncate">{lead.website}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5 truncate">
              <Mail className="h-3.5 w-3.5 shrink-0 text-primary" />
              {lead.email ? (
                <span className="truncate">{lead.email}</span>
              ) : (
                <span className="italic text-[#b45309]">Ingen e-post funnet</span>
              )}
              {lead.email && lead.email_source && (
                <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold uppercase ${
                  lead.email_source === "nettside" ? "bg-[#DCFCE7] text-[#15803d]" :
                  lead.email_source === "brreg" ? "bg-[#DBEAFE] text-[#1d4ed8]" :
                  "bg-[#E0E7FF] text-[#4338ca]"
                }`}>
                  {lead.email_source === "nettside" ? "Nettside" : lead.email_source === "brreg" ? "Brønnøysund" : "Funnet via søk"}
                </span>
              )}
            </div>
            {lead.contact_name && (
              <div className="flex items-center gap-1.5 truncate">
                <User className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="truncate">{(lead.contact_role || "Kontakt") + ": " + lead.contact_name}</span>
              </div>
            )}
            {lead.phone && (
              <div className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span>{lead.phone}</span>
              </div>
            )}
          </div>

          <div
            data-testid="lead-need-tag"
            className="mt-2.5 inline-block rounded-md bg-[#DBEAFE] dark:bg-[#1e3a5f] px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-[#1d4ed8] dark:text-[#93c5fd]"
          >
            Mulig behov: {lead.possible_need}
          </div>

          {lead.ai_summary && (
            <p
              data-testid="lead-ai-summary"
              className={`mt-2.5 italic text-foreground/80 ${compact ? "text-xs" : "text-sm"} leading-snug`}
            >
              "{lead.ai_summary}"
            </p>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              data-testid="lead-preview-email-button"
              size="sm"
              variant={expanded ? "default" : "outline"}
              onClick={() => onToggleEmail(lead.id)}
              className="gap-1.5 h-8"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
              {expanded ? "SKJUL E-POST" : "SE E-POST"}
            </Button>
            <Button
              data-testid="lead-approve-button"
              size="sm"
              onClick={() => onStatus(lead.id, "godkjent")}
              className={`gap-1.5 h-8 ${lead.status === "godkjent" ? "bg-[#16A34A] hover:bg-[#15803d]" : "bg-[#16A34A]/90 hover:bg-[#16A34A]"} text-white`}
            >
              <Check className="h-4 w-4" /> GODKJENN
            </Button>
            <Button
              data-testid="lead-reject-button"
              size="sm"
              variant="outline"
              onClick={() => onStatus(lead.id, "avvist")}
              className="gap-1.5 h-8 text-destructive border-destructive/40 hover:bg-destructive/10"
            >
              <X className="h-4 w-4" /> AVVIS
            </Button>
            {lead.status === "godkjent" && (
              <Button
                data-testid="lead-to-customer-button"
                size="sm"
                variant="outline"
                disabled={!!lead.converted_customer_id}
                onClick={async () => {
                  await saas.fromLead(lead.id);
                  toast.success(`${lead.name} lagt til i kunderegisteret`);
                  onChange({ ...lead, converted_customer_id: "1" });
                }}
                className="gap-1.5 h-8"
              >
                <UserPlus className="h-4 w-4" /> {lead.converted_customer_id ? "ER KUNDE" : "LEGG TIL SOM KUNDE"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <EmailPanel lead={lead} onChange={onChange} onStatus={onStatus} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
